import { useEffect, useRef } from 'react';
import { useReducedMotion } from './useReducedMotion';


const POINTER_VARS = ['--mx', '--my', '--rx', '--ry', '--px', '--py', '--dist', '--glare'];

export function useTilt<T extends HTMLElement>(enabled = true, strength = 1, pointer = false) {
  const ref = useRef<T>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const element = ref.current;
    if (!element || reducedMotion || (!enabled && !pointer)) return;

    let frame = 0;
    let last: { x: number; y: number } | null = null;

    const apply = () => {
      frame = 0;
      if (!last) return;
      const rect = element.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (last.x - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (last.y - rect.top) / rect.height));
      const px = (x - 0.5) * 2;
      const py = (y - 0.5) * 2;
      element.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
      element.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
      element.style.setProperty('--px', px.toFixed(3));
      element.style.setProperty('--py', py.toFixed(3));
      element.style.setProperty('--dist', Math.min(1, Math.hypot(px, py)).toFixed(3));
      if (enabled) {
        element.style.setProperty('--rx', `${((0.5 - y) * 16 * strength).toFixed(2)}deg`);
        element.style.setProperty('--ry', `${((x - 0.5) * 20 * strength).toFixed(2)}deg`);
      }
      element.style.setProperty('--glare', '1');
      element.dataset.tilting = 'true';
    };

    const onMove = (event: PointerEvent) => {
      last = { x: event.clientX, y: event.clientY };
      if (!frame) frame = requestAnimationFrame(apply);
    };

    const reset = () => {
      last = null;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      for (const name of POINTER_VARS) element.style.removeProperty(name);
      delete element.dataset.tilting;
    };

    const onUp = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') reset();
    };

    element.addEventListener('pointerenter', onMove);
    element.addEventListener('pointermove', onMove);
    element.addEventListener('pointerleave', reset);
    element.addEventListener('pointercancel', reset);
    element.addEventListener('pointerup', onUp);
    return () => {
      reset();
      element.removeEventListener('pointerenter', onMove);
      element.removeEventListener('pointermove', onMove);
      element.removeEventListener('pointerleave', reset);
      element.removeEventListener('pointercancel', reset);
      element.removeEventListener('pointerup', onUp);
    };
  }, [enabled, pointer, reducedMotion, strength]);

  return ref;
}
